import axios from "axios";
import { User } from "../models/User";
import { SimCard } from "../models/SimCard";
import { ImsiMap } from "../models/ImsiMap";
import { EsimNumber } from "../models/Number";
import { EsimOrder } from "../models/EsimOrder";
import { OfferNumber } from "../models/OfferNumber";
import {
  ConsumptionResponse,
  ConsumptionStatsResponse,
  ConsumptionTypesResponse,
  ConsumptionFilters,
} from "../models/Consumption";

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "https://api.connect-esim.com/api",
  headers: {
    "Content-Type": "application/json",
  },
});

// Flag to prevent duplicate token initialization
let tokenInitialized = false;

// Function to add auth token to requests
export const addAuthToken = (token: string | null) => {
  if (token) {
    apiClient.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    delete apiClient.defaults.headers.common["Authorization"];
  }
};

// Initialize auth token from localStorage on app load (only once)
export const initializeAuthToken = () => {
  if (tokenInitialized) {
    return localStorage.getItem("auth_token");
  }

  const token = localStorage.getItem("auth_token");
  if (token) {
    addAuthToken(token);
    tokenInitialized = true;
  }
  return token;
};

// Request interceptor to dynamically get token from localStorage (optimized)
apiClient.interceptors.request.use(
  (config) => {
    // Skip interceptor for auth endpoints to avoid circular dependencies
    if (config.url?.includes("/admin/login")) {
      return config;
    }

    const token = localStorage.getItem("auth_token");
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor to auto-logout on 401 Unauthorized
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && window.location.pathname !== '/login') {
      localStorage.removeItem('auth_token');
      addAuthToken(null);
      window.location.href = '/login';
    }
    return Promise.reject(error);
  },
);

export interface UserFilters {
  search?: string;
  id?: string;
  id_from?: string;
  id_to?: string;
  name?: string;
  email?: string;
  user_type?: string;
  active?: string;
  created_from?: string;
  created_to?: string;
  balance_min?: string;
  balance_max?: string;
  balance_negative?: string;
  has_esim?: string;
  /** 1-based. The server pages the list; the browser no longer holds it all. */
  page?: string;
  limit?: string;
  sort_by?: string;
  sort_order?: string;
}

/** A page of results plus the count the pager needs. */
export interface Page<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}

export interface BillingType {
  user_type: number;
  desc: string;
}

export const fetchUsers = async (filters?: UserFilters): Promise<Page<User>> => {
  const params: Record<string, string> = {};
  if (filters) {
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== "") params[k] = v;
    });
  }
  const response = await apiClient.get("/admin/users", { params });
  return response.data;
};

export const fetchUserFilterOptions = async (): Promise<BillingType[]> => {
  const response = await apiClient.get("/admin/users/options");
  return response.data;
};

export const loginUser = async (credentials: any) => {
  const response = await apiClient.post("/admin/login", credentials);
  return response.data;
};

export const createUser = async (userData: Omit<User, "id" | "created_at">) => {
  const response = await apiClient.post("/admin/users", userData);
  return response.data;
};

export const updateUser = async (id: number, userData: Partial<User>) => {
  console.log("API updateUser called with:", {
    id,
    userData: JSON.stringify(userData, null, 2),
  });

  // Sanitize payload before sending to backend:
  // - Convert empty strings ("") to null
  // - Convert boolean-like strings ("true"/"false"/"1"/"0") to actual booleans
  // - Recursively sanitize arrays and objects
  const sanitize = (value: any): any => {
    if (value === undefined) return undefined;
    if (value === null) return null;

    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed === "") return null;
      const low = trimmed.toLowerCase();
      if (low === "true" || trimmed === "1") return true;
      if (low === "false" || trimmed === "0") return false;
      return value;
    }

    if (Array.isArray(value)) {
      return value.map((v) => sanitize(v));
    }

    if (typeof value === "object") {
      const out: any = {};
      Object.keys(value).forEach((k) => {
        out[k] = sanitize(value[k]);
      });
      return out;
    }

    // number, boolean, etc.
    return value;
  };

  const sanitizedData = sanitize(userData);

  try {
    const response = await apiClient.put(`/admin/users/${id}`, sanitizedData);
    console.log("API updateUser response:", response.data);
    return response.data;
  } catch (error) {
    console.error("API updateUser error:", error);
    throw error;
  }
};

export const deleteUser = async (id: number) => {
  await apiClient.delete(`/admin/users/${id}`);
};

export interface ListParams {
  page?: number;
  limit?: number;
  sort_by?: string;
  sort_order?: string;
  /** SIM cards only: which status tab is selected. */
  status?: string;
  /** Numbers only: substring match on the number. */
  search?: string;
}

/** Drop empty values so the server sees only what was actually set. */
const listQuery = (p?: ListParams): Record<string, string> => {
  const out: Record<string, string> = {};
  if (!p) return out;
  Object.entries(p).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") out[k] = String(v);
  });
  return out;
};

export interface SimCardPage extends Page<SimCard> {
  counts: { all: number; active: number; inactive: number; awaiting: number };
}

export const fetchSimCards = async (p?: ListParams): Promise<SimCardPage> => {
  const response = await apiClient.get("/admin/sim-cards", { params: listQuery(p) });
  return response.data;
};

export const createSimCard = async (
  simCardData: Omit<SimCard, "id" | "activation_time" | "last_update">,
) => {
  const response = await apiClient.post("/admin/sim-cards", simCardData);
  return response.data;
};

export const updateSimCard = async (
  id: number,
  simCardData: Partial<SimCard>,
) => {
  const response = await apiClient.put(`/admin/sim-cards/${id}`, simCardData);
  return response.data;
};

export const deleteSimCard = async (id: number) => {
  await apiClient.delete(`/admin/sim-cards/${id}`);
};

export const fetchImsiMaps = async (p?: ListParams): Promise<Page<ImsiMap>> => {
  const response = await apiClient.get("/admin/imsi-maps", { params: listQuery(p) });
  return response.data;
};

export const deleteImsiMap = async (id: number) => {
  await apiClient.delete(`/admin/imsi-maps/${id}`);
};

export const fetchNumbers = async (p?: ListParams): Promise<Page<EsimNumber>> => {
  const response = await apiClient.get("/admin/numbers", { params: listQuery(p) });
  return response.data;
};

export const createNumber = async (
  numberData: Omit<
    EsimNumber,
    "id" | "created" | "expiration_date" | "auto_renew"
  >,
) => {
  const response = await apiClient.post("/admin/numbers", numberData);
  return response.data;
};

export const updateNumber = async (
  id: number,
  numberData: Partial<EsimNumber>,
) => {
  const response = await apiClient.put(`/admin/numbers/${id}`, numberData);
  return response.data;
};

export const deleteNumber = async (id: number) => {
  await apiClient.delete(`/admin/numbers/${id}`);
};

export const updateUserBalance = async (
  userId: number,
  balanceIndex: number,
  amount: number,
) => {
  const response = await apiClient.put(
    `/admin/users/${userId}/balance/${balanceIndex}`,
    { amount },
  );
  return response.data;
};


export const addUserBalance = async (
  userId: number,
  balanceIndex: number,
  amountUnits: number,
) => {
  const response = await apiClient.post(
    `/admin/users/${userId}/balance/${balanceIndex}/add`,
    { amount: amountUnits },
  );
  return response.data;
};
export const fetchDashboardStats = async () => {
  const response = await apiClient.get("/admin/dashboard-stats");
  return response.data;
};

export const fetchEsimOrders = async (p?: ListParams): Promise<Page<EsimOrder>> => {
  const response = await apiClient.get("/admin/esim-orders", { params: listQuery(p) });
  return response.data;
};

export const createEsimOrder = async (
  esimOrderData: Omit<EsimOrder, "id" | "insert_date_time">,
) => {
  const response = await apiClient.post("/admin/esim-orders", esimOrderData);
  return response.data;
};

export const updateEsimOrder = async (
  id: number,
  esimOrderData: Partial<EsimOrder>,
) => {
  const response = await apiClient.put(
    `/admin/esim-orders/${id}`,
    esimOrderData,
  );
  return response.data;
};

export const deleteEsimOrder = async (id: number) => {
  await apiClient.delete(`/admin/esim-orders/${id}`);
};

// Consumption API functions
export const fetchConsumption = async (
  filters: ConsumptionFilters,
): Promise<ConsumptionResponse> => {
  const params = new URLSearchParams({
    type: filters.type,
    page: filters.page.toString(),
    limit: filters.limit.toString(),
  });

  // Only include billing_id if it has a value
  if (filters.billingId) params.append("billing_id", filters.billingId);
  if (filters.dateFrom) params.append("date_from", filters.dateFrom);
  if (filters.dateTo) params.append("date_to", filters.dateTo);
  if (filters.countries && filters.countries.length > 0) {
    filters.countries.forEach((c) => params.append("countries[]", c));
  }
  if (filters.sortBy) params.append("sort_by", filters.sortBy);
  if (filters.sortOrder) params.append("sort_order", filters.sortOrder);
  if (filters.brandSlug) params.append("brand_slug", filters.brandSlug);

  const response = await apiClient.get(
    `/admin/consumption?${params.toString()}`,
  );
  return response.data;
};

export const fetchConsumptionStats = async (
  billingId: string,
  dateFrom?: string,
  dateTo?: string,
): Promise<ConsumptionStatsResponse> => {
  const params = new URLSearchParams({
    billing_id: billingId,
  });

  if (dateFrom) params.append("date_from", dateFrom);
  if (dateTo) params.append("date_to", dateTo);

  const response = await apiClient.get(
    `/admin/consumption/stats?${params.toString()}`,
  );
  return response.data;
};

export const fetchConsumptionTypes = async (
  billingId?: string,
): Promise<ConsumptionTypesResponse> => {
  const params = billingId ? `?billing_id=${billingId}` : '';
  const response = await apiClient.get(`/admin/consumption/types${params}`);
  return response.data;
};

export const fetchConsumptionByDate = async (
  billingId: string,
  dateFrom: string,
  dateTo: string,
  page: number = 1,
  limit: number = 50,
): Promise<ConsumptionResponse> => {
  const params = new URLSearchParams({
    billing_id: billingId,
    date_from: dateFrom,
    date_to: dateTo,
    page: page.toString(),
    limit: limit.toString(),
  });

  const response = await apiClient.get(
    `/admin/consumption/by-date?${params.toString()}`,
  );
  return response.data;
};

export const expendConsumption = async (billingId: number, amount: number, comment: string) => {
  const response = await apiClient.post("/admin/consumption/expend", {
    billing_id: billingId,
    amount,
    comment,
  });
  return response.data;
};


export const fetchConsumptionCountries = async (
  billingId?: string,
  dateFrom?: string,
  dateTo?: string,
): Promise<{ data: string[]; billing_id: string | null }> => {
  const params = new URLSearchParams();
  if (billingId) params.append("billing_id", billingId);
  if (dateFrom) params.append("date_from", dateFrom);
  if (dateTo) params.append("date_to", dateTo);
  const response = await apiClient.get(`/admin/consumption/countries?${params.toString()}`);
  return response.data;
};

export const fetchConsumptionByCountry = async (
  billingId?: string,
  dateFrom?: string,
  dateTo?: string,
): Promise<{ data: Array<{ country: string; internet_bytes: string; voice_seconds: string; sms_count: string; transaction_count: string }> }> => {
  const params = new URLSearchParams();
  if (billingId) params.append("billing_id", billingId);
  if (dateFrom) params.append("date_from", dateFrom);
  if (dateTo) params.append("date_to", dateTo);
  const response = await apiClient.get(`/admin/consumption/by-country?${params.toString()}`);
  return response.data;
};

export const fetchConsumptionByUser = async (
  dateFrom?: string,
  dateTo?: string,
  page = 1,
  limit = 50,
): Promise<{
  data: Array<{ account_id: number; user_id: number | null; email: string | null; total_money: string; total_data_bytes: string; total_voice_seconds: string; total_sms: string; transaction_count: string }>;
  pagination: { current_page: number; per_page: number; total_records: number; total_pages: number; has_next: boolean; has_prev: boolean };
}> => {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (dateFrom) params.append("date_from", dateFrom);
  if (dateTo) params.append("date_to", dateTo);
  const response = await apiClient.get(`/admin/consumption/by-user?${params.toString()}`);
  return response.data;
};

// Push Notification API functions
export const fetchUsersWithFirebaseId = async () => {
  const response = await apiClient.get("/admin/push-notifications/users");
  return response.data;
};

export const sendPushNotification = async (data: {
  user_ids: number[];
  title: string;
  body: string;
  data?: Record<string, any>;
}) => {
  const response = await apiClient.post(
    "/admin/push-notifications/send",
    data,
  );
  return response.data;
};

export const sendPushNotificationToTopic = async (data: {
  topic: string;
  title: string;
  body: string;
  data?: Record<string, any>;
}) => {
  const response = await apiClient.post(
    "/admin/push-notifications/send-topic",
    data,
  );
  return response.data;
};

// Logout function
export const logoutUser = () => {
  localStorage.removeItem("auth_token");
  addAuthToken(null);
};

// Offer Numbers API functions
export const fetchOfferNumbers = async (): Promise<OfferNumber[]> => {
  const response = await apiClient.get("/admin/offer-numbers");
  return response.data;
};

export const createOfferNumber = async (
  data: Omit<OfferNumber, "id">,
): Promise<OfferNumber> => {
  const response = await apiClient.post("/admin/offer-numbers", data);
  return response.data;
};

export const updateOfferNumber = async (
  id: number,
  data: Partial<OfferNumber>,
): Promise<OfferNumber> => {
  const response = await apiClient.put(`/admin/offer-numbers/${id}`, data);
  return response.data;
};

export const deleteOfferNumber = async (id: number): Promise<void> => {
  await apiClient.delete(`/admin/offer-numbers/${id}`);
};

// IMSI Map CRUD API functions
export const createImsiMap = async (
  data: Omit<ImsiMap, "id">,
): Promise<ImsiMap> => {
  const response = await apiClient.post("/admin/imsi-maps", data);
  return response.data;
};

export const updateImsiMap = async (
  id: number,
  data: Partial<ImsiMap>,
): Promise<ImsiMap> => {
  const response = await apiClient.put(`/admin/imsi-maps/${id}`, data);
  return response.data;
};

// Free eSIM assignment API functions
export const assignFreeEsim = async (
  userId: number,
  data: { region: string; label?: string },
): Promise<{ success: boolean; message: string; esim: EsimOrder }> => {
  const response = await apiClient.post(
    `/admin/users/${userId}/assign-free-esim`,
    data,
  );
  return response.data;
};

export const fetchAvailableEsims = async (
  region?: string,
): Promise<{ count: number; esims: EsimOrder[] }> => {
  const params = region ? `?region=${region}` : "";
  const response = await apiClient.get(`/admin/available-esims${params}`);
  return response.data;
};


// ── Brand stock pools & transfers (superadmin only) ──────────────────────

export interface BrandStockPool {
  brand_id: number;
  slug: string;
  name: string;
  available: number;
  issued: number;
}

export interface StockPoolsResponse {
  available_esim_orders: number;
  brand_pools?: BrandStockPool[];
  shared_pool?: number;
  available_numbers_by_region?: Array<{ country: string; count: number }>;
}

/** Per-brand card stock. Same endpoint as fetchAvailableEsims, pool-shaped. */
export const fetchStockPools = async (): Promise<StockPoolsResponse> => {
  const response = await apiClient.get("/admin/available-esims");
  return response.data;
};

export interface StockTransferPayload {
  from_slug: string;
  to_slug: string;
  count: number;
}

export interface StockTransferResult {
  moved: number;
  from: { slug: string; available: number };
  to: { slug: string; available: number };
}

/**
 * Moves `count` *available* (unassigned, un-issued) cards from one brand pool
 * to another. Superadmin only; the backend rejects anything else with 403.
 */
export const transferStock = async (
  payload: StockTransferPayload,
): Promise<StockTransferResult> => {
  const response = await apiClient.post("/admin/stock/transfer", payload);
  return response.data;
};

/**
 * IMSI range assignment payload.
 *
 * `imsi_from` / `imsi_to` are 15-digit numbers sitting at the edge of the JS
 * safe integer range — they must stay strings all the way to the wire. Never
 * Number()/parseInt() them.
 */
export interface StockAssignRangePayload {
  imsi_from: string;
  imsi_to: string;
  to_slug: string;
  dry_run: boolean;
}

/** Where the matched cards sit today, per owning brand. */
export interface StockRangeBrandBreakdown {
  slug: string;
  available: number;
  issued: number;
}

export interface StockAssignRangeResult {
  dry_run: boolean;
  imsi_from: string;
  imsi_to: string;
  to: { slug: string; name: string };
  /** esim_orders cards whose mb_sim.imsi falls in the range. */
  matched: number;
  /** Of those: available and not already on the target brand. */
  assignable: number;
  /** Available cards already owned by the target brand. */
  already_owned: number;
  /** Issued / assigned cards in range — never moved. */
  skipped_issued: number;
  /** mb_sim rows in range with no esim_orders row — cannot be sold. */
  in_hlr_without_profile: number;
  /** Cards that will move but carry no activation profile, so they cannot be sold yet. */
  assignable_without_profile?: number;
  by_current_brand: StockRangeBrandBreakdown[];
  /** 0 when dry_run is true. */
  moved: number;
}

/**
 * Assigns every *available* card whose IMSI falls inside [imsi_from, imsi_to]
 * to the target brand. Issued cards are never touched. Superadmin only; the
 * backend rejects anything else with 403.
 *
 * Call with dry_run: true first — the response shape is identical, only
 * `moved` differs — and show the operator what would happen before committing.
 */
export const assignStockRange = async (
  payload: StockAssignRangePayload,
): Promise<StockAssignRangeResult> => {
  const response = await apiClient.post("/admin/stock/assign-range", payload);
  return response.data;
};


// ── Billing API ──────────────────────────────────────────────────────────

export interface BillingStatsParams {
  date_from: string;
  date_to: string;
  brand_slug?: string;
}

export interface BillingTxParams extends BillingStatsParams {
  method?: string;
  status?: string;
  search?: string;
  page?: number;
  limit?: number;
  sort?: string;
  dir?: string;
}

export const fetchBillingStats = async (params: BillingStatsParams) => {
  const r = await apiClient.get("/admin/billing/stats", { params });
  return r.data;
};

export const fetchBillingTransactions = async (params: BillingTxParams) => {
  const r = await apiClient.get("/admin/billing/transactions", { params });
  return r.data;
};

export const fetchBillingDailySummary = async (params: BillingStatsParams) => {
  const r = await apiClient.get("/admin/billing/summary/daily", { params });
  return r.data;
};

export const fetchBillingUserSummary = async (params: BillingTxParams) => {
  const r = await apiClient.get("/admin/billing/summary/users", { params });
  return r.data;
};

export default apiClient;


// ── Admin Users + Password Reset ──────────────────────────────────────────

export interface AdminUserRecord {
  id: number;
  username: string;
  role: string;
  brand_slug?: string | null;
  created_at: string;
}

export const fetchAdminUsers = async (): Promise<AdminUserRecord[]> => {
  const r = await apiClient.get('/admin/admins');
  return r.data;
};

export interface ResetPasswordPayload {
  password?: string;
  generate?: boolean;
}

export interface ResetPasswordResult {
  success: boolean;
  message: string;
  generated_password?: string;
}

export const resetUserPassword = async (
  userId: number,
  payload: ResetPasswordPayload,
): Promise<ResetPasswordResult> => {
  const r = await apiClient.post(`/admin/users/${userId}/password`, payload);
  return r.data;
};

export const resetAdminPassword = async (
  adminId: number,
  payload: ResetPasswordPayload,
): Promise<ResetPasswordResult> => {
  const r = await apiClient.post(`/admin/admins/${adminId}/password`, payload);
  return r.data;
};


// -- User eSIM Orders --

export const fetchUserEsimOrders = async (userId: number): Promise<EsimOrder[]> => {
  const response = await apiClient.get(`/admin/users/${userId}/esim-orders`);
  return response.data;
};


// -- Consumption by billing profile --

export interface BillingProfileStat {
  billing_profile_name: string;
  total_charged: string;
  total_credited: string;
  transaction_count: string;
}

export const fetchConsumptionByBillingProfile = async (
  billingId?: string,
  dateFrom?: string,
  dateTo?: string,
): Promise<{ data: BillingProfileStat[] }> => {
  const params = new URLSearchParams();
  if (billingId) params.append('billing_id', billingId);
  if (dateFrom) params.append('date_from', dateFrom);
  if (dateTo) params.append('date_to', dateTo);
  const query = params.toString() ? '?' + params.toString() : '';
  const response = await apiClient.get('/admin/consumption/by-billing-profile' + query);
  return response.data;
};


// -- Brand config --

export interface BrandConfig {
  name: string;
  logo_url: string | null;
  primary_color: string | null;
  secondary_color: string | null;
}

export const fetchBrandConfig = async (slug?: string | null): Promise<BrandConfig> => {
  const url = slug ? `/admin/brand/config?slug=${encodeURIComponent(slug)}` : "/admin/brand/config";
  const response = await apiClient.get(url);
  return response.data;
};

export const decodeTokenBrandSlug = (token: string): string | null => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload?.data?.brand_slug ?? null;
  } catch {
    return null;
  }
};

// Returns true when the JWT is malformed or its `exp` claim is in the past.
export const isTokenExpired = (token: string | null): boolean => {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    if (!payload?.exp) return false; // no expiry claim → treat as non-expiring
    return payload.exp * 1000 <= Date.now();
  } catch {
    return true;
  }
};

// Reset white-label brand CSS variables back to the neutral defaults declared
// in index.css so a previous brand's palette / logo does not persist.
export const resetBrandCssVars = (): void => {
  const root = document.documentElement.style;
  root.setProperty("--color-primary", "var(--ink)");
  root.setProperty("--color-secondary", "var(--accent)");
  root.setProperty("--brand-logo-url", "none");
};

export interface BrandOption {
  slug: string;
  name: string;
}

export const fetchBrands = async (): Promise<BrandOption[]> => {
  const r = await apiClient.get("/admin/brands");
  return r.data?.data ?? [];
};


// -- Admin user management --

export interface CreateAdminPayload {
  username: string;
  brand_slug?: string | null;
  password?: string;
  generate_password?: boolean;
}

export interface CreateAdminResult {
  success: boolean;
  admin: AdminUserRecord & { brand_slug?: string | null };
  generated_password?: string;
}

export const createAdminUser = async (payload: CreateAdminPayload): Promise<CreateAdminResult> => {
  const r = await apiClient.post('/admin/admins', payload);
  return r.data;
};

export const deleteAdminUser = async (id: number): Promise<void> => {
  await apiClient.delete(`/admin/admins/${id}`);
};

// ---------------------------------------------------------------- Partner API keys

export interface PartnerApiKeyRecord {
  id: number;
  brand_id: number;
  brand_slug: string;
  key_prefix: string;
  mode: "live" | "test";
  scopes: string[];
  label: string | null;
  active: boolean;
  created_at: string;
  created_by: string | null;
  last_used_at: string | null;
  revoked_at: string | null;
}

export const PARTNER_SCOPES = [
  "sims:read", "sims:write",
  "customers:read", "customers:write",
  "billing:write",
  "usage:read",
] as const;

export const fetchPartnerKeys = async (): Promise<PartnerApiKeyRecord[]> => {
  const response = await apiClient.get("/admin/partner-keys");
  return response.data.data ?? [];
};

export const createPartnerKey = async (payload: {
  mode: "live" | "test";
  brand_slug?: string | null;
  scopes?: string[];
  label?: string | null;
}): Promise<{ key: string; record: PartnerApiKeyRecord; notice: string }> => {
  const response = await apiClient.post("/admin/partner-keys", payload);
  return response.data;
};

export const revokePartnerKey = async (id: number): Promise<void> => {
  await apiClient.delete(`/admin/partner-keys/${id}`);
};

// ---------------------------------------------------------------- USSD menus

export interface UssdMenuItem {
  key: string;
  label: string;
  action: "balance" | "msisdn" | "imsi" | "iccid" | "bundles_list" | "text" | "submenu";
  text?: string;
  items?: UssdMenuItem[];
}

export interface UssdScreen {
  name: string;
  text: string;
  length: number;
  limit: number;
  encoding: "GSM-7" | "UCS-2";
}

export interface UssdMenu {
  code: string;
  language: "en" | "sr";
  title: string | null;
  items: UssdMenuItem[];
  active: boolean;
  version: number;
  updated_at: string;
  screens: UssdScreen[];
}

export const fetchUssdMenus = async (slug: string): Promise<UssdMenu[]> => {
  const response = await apiClient.get(`/admin/brands/${encodeURIComponent(slug)}/ussd-menu`);
  return response.data.data ?? [];
};

export const saveUssdMenu = async (
  slug: string,
  menu: { code: string; language: string; title: string | null; items: unknown; active: boolean }
): Promise<UssdMenu> => {
  const response = await apiClient.put(`/admin/brands/${encodeURIComponent(slug)}/ussd-menu`, menu);
  return response.data;
};

export const deleteUssdMenu = async (slug: string, code: string): Promise<void> => {
  await apiClient.delete(`/admin/brands/${encodeURIComponent(slug)}/ussd-menu`, { params: { code } });
};

// ------------------------------------------------------------- Partner SMS

export interface PartnerSmsRow {
  id: string;
  brand: string;
  brand_name: string;
  customer_id: number | null;
  to: string;
  from: string;
  text: string;
  encoding: "GSM-7" | "UCS-2";
  parts: number;
  status: "queued" | "sent" | "delivered" | "failed";
  error: string | null;
  reference: string | null;
  created_at: string;
  delivered_at: string | null;
}

export interface PartnerSmsSummary {
  brand: string;
  brand_name: string;
  daily_limit: number;
  per_minute: number;
  total: number;
  last_24h: number;
  delivered: number;
  failed: number;
  pending: number;
  parts: number;
  last_sent: string | null;
}

export const fetchPartnerSms = async (params: {
  brand?: string; status?: string; search?: string; from?: string; to?: string; page: number; per_page: number;
}): Promise<{ data: PartnerSmsRow[]; total: number; summary: PartnerSmsSummary[] }> => {
  const clean = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== "" && v !== undefined));
  const response = await apiClient.get("/admin/partner-sms", { params: clean });
  return response.data;
};

// ------------------------------------------------------------- SMS senders

export interface SmsSenderRow {
  id: string;
  sender_name: string;
  /** "number": one of our numbers, assigned to the brand by us. */
  kind: "name" | "number";
  status: "pending" | "approved" | "rejected" | "revoked";
  reference: string | null;
  reason: string | null;
  created_at: string;
  decided_at: string | null;
  decided_by: string | null;
  brand: string;
  brand_name: string;
}

export const fetchSmsSenders = async (params: { brand?: string; status?: string } = {}): Promise<SmsSenderRow[]> => {
  const clean = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== "" && v !== undefined));
  const response = await apiClient.get("/admin/sms-senders", { params: clean });
  return response.data.data;
};

/** Our decision on a sender name a partner registered. A reason is required unless approving. */
export const decideSmsSender = async (
  id: string, action: "approve" | "reject" | "revoke", reason?: string,
): Promise<SmsSenderRow> => {
  const response = await apiClient.post(`/admin/sms-senders/${id}/${action}`, reason ? { reason } : {});
  return response.data;
};
